import { TimeUnit, TrendCurrentPeriod } from "@repo/common"
import { TrendMetricKey } from "@repo/ui/components/stats/key-figures-display"
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMinute,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns"
import { useState } from "react"

export const TREND_PERIODS = [
  "last7Days",
  "last30Days",
  "last12Weeks",
  "last12Months",
  "currentWeek",
  "currentMonth",
  "currentYear",
] as const

type TrendPreset = (typeof TREND_PERIODS)[number]

/** A preset, or the dates someone picked by hand. */
export type TrendPeriod = TrendPreset | "custom"

/** Each period has the bars that read best over that span. */
const TIME_UNIT_BY_PERIOD: Record<TrendPreset, TimeUnit> = {
  last7Days: "day",
  last30Days: "day",
  last12Weeks: "week",
  last12Months: "month",
  currentWeek: "day",
  currentMonth: "day",
  currentYear: "month",
}

const CURRENT_PERIODS: Partial<Record<TrendPreset, TrendCurrentPeriod>> = {
  currentWeek: "week",
  currentMonth: "month",
  currentYear: "year",
}

/** Rolling spans of another length than their time unit's standard. */
const ROLLING_PERIODS: Partial<Record<TrendPreset, string>> = {
  last30Days: "30",
  last12Weeks: "12",
}

/** How the period is asked for, beyond its time unit and end date. */
const toPresetSpanParams = (period: TrendPreset): Record<string, string> => {
  const currentPeriod = CURRENT_PERIODS[period]
  if (currentPeriod) return { currentPeriod }
  const rollingPeriods = ROLLING_PERIODS[period]
  return rollingPeriods ? { rollingPeriods } : {}
}

export interface TrendDateRange {
  from: Date
  to: Date
}

const MONDAY = { weekStartsOn: 1 } as const

/**
 * What each period covers, the same as the trends' bars. Current periods are
 * whole, so they compare with the whole one before, as a calendar period does.
 */
const DATE_RANGE_BY_PRESET: Record<TrendPreset, (now: Date) => TrendDateRange> =
  {
    last7Days: (now) => ({
      from: startOfDay(subDays(now, 6)),
      to: endOfDay(now),
    }),
    last30Days: (now) => ({
      from: startOfDay(subDays(now, 29)),
      to: endOfDay(now),
    }),
    last12Weeks: (now) => ({
      from: startOfWeek(subWeeks(now, 11), MONDAY),
      to: endOfWeek(now, MONDAY),
    }),
    last12Months: (now) => ({
      from: startOfMonth(subMonths(now, 11)),
      to: endOfMonth(now),
    }),
    currentWeek: (now) => ({
      from: startOfWeek(now, MONDAY),
      to: endOfWeek(now, MONDAY),
    }),
    currentMonth: (now) => ({ from: startOfMonth(now), to: endOfMonth(now) }),
    currentYear: (now) => ({ from: startOfYear(now), to: endOfYear(now) }),
  }

export const DEFAULT_TREND_PERIOD = "last30Days"

export const isTrendPeriod = (value: string): value is TrendPeriod =>
  value === "custom" || TREND_PERIODS.some((period) => period === value)

const MAX_DAYS_IN_BARS = 31
const MAX_MONTHS_IN_BARS = 12

/** The bars that fit a range of any length, within what the API can split. */
const toRangeTimeUnit = ({ from, to }: TrendDateRange): TimeUnit => {
  if (differenceInCalendarDays(to, from) < MAX_DAYS_IN_BARS) return "day"
  return differenceInCalendarMonths(to, from) < MAX_MONTHS_IN_BARS
    ? "month"
    : "year"
}

/** A single period opened up into shorter ones. */
export interface TrendZoom {
  /** Names the period, e.g. "Mar 2026". */
  label: string
  startDate: Date
  endDate: Date
}

export interface TrendControls {
  period: TrendPeriod
  setPeriod: (period: TrendPeriod) => void
  setCustomRange: (range: TrendDateRange) => void
  timeUnit: TimeUnit
  /** Query parameters for the span, when it isn't the time unit's standard. */
  spanParams: Record<string, string>
  /** Fixed for the page's lifetime, since it is part of the query key. */
  endDate: Date
  /** The span as dates, which the key figures are asked for. */
  dateRange: TrendDateRange
  zoom: TrendZoom | null
  zoomIn: (zoom: TrendZoom) => void
  zoomOut: () => void
  /** The metric open in the big chart. */
  focusedMetric: TrendMetricKey | null
  focusMetric: (key: TrendMetricKey) => void
  closeMetric: () => void
}

/** What is picked: a preset, or the dates of a custom range. */
export type TrendSelection =
  | { period: TrendPreset }
  | { period: "custom"; range: TrendDateRange }

interface TrendControlsOptions {
  initialSelection?: TrendSelection
  /** E.g. to keep the selection in the URL. */
  onSelectionChange?: (selection: TrendSelection) => void
}

/** What a selection asks the API for. */
export interface TrendRequest {
  timeUnit: TimeUnit
  spanParams: Record<string, string>
  endDate: Date
  dateRange: TrendDateRange
}

/** Whole minutes, so a prefetch and the page ask for the same thing. */
const toTrendNow = (): Date => startOfMinute(new Date())

/** Also for fetching a page's numbers ahead of it. */
export const getTrendRequest = (
  selection: TrendSelection,
  now: Date = toTrendNow(),
): TrendRequest => {
  if (selection.period === "custom") {
    return {
      timeUnit: toRangeTimeUnit(selection.range),
      spanParams: { startDate: selection.range.from.toISOString() },
      endDate: selection.range.to,
      dateRange: selection.range,
    }
  }
  return {
    timeUnit: TIME_UNIT_BY_PERIOD[selection.period],
    spanParams: toPresetSpanParams(selection.period),
    endDate: now,
    dateRange: DATE_RANGE_BY_PRESET[selection.period](now),
  }
}

/** The span of the whole page, and what the big chart has open. */
export const useTrendControls = ({
  initialSelection = { period: DEFAULT_TREND_PERIOD },
  onSelectionChange,
}: TrendControlsOptions = {}): TrendControls => {
  const [selection, setSelection] = useState(initialSelection)
  const [now] = useState(toTrendNow)
  const [zoom, setZoom] = useState<TrendZoom | null>(null)
  const [focusedMetric, setFocusedMetric] = useState<TrendMetricKey | null>(
    null,
  )

  const selectedRange = getTrendRequest(selection, now).dateRange
  const { dateRange, timeUnit, spanParams, endDate } = getTrendRequest(
    zoom
      ? { period: "custom", range: { from: zoom.startDate, to: zoom.endDate } }
      : selection,
    now,
  )

  const select = (next: TrendSelection) => {
    setSelection(next)
    setZoom(null)
    onSelectionChange?.(next)
  }

  const setPeriod = (next: TrendPeriod) =>
    select(
      next === "custom"
        ? { period: next, range: selectedRange }
        : { period: next },
    )

  return {
    period: selection.period,
    setPeriod,
    setCustomRange: (range) => select({ period: "custom", range }),
    timeUnit,
    spanParams,
    endDate,
    dateRange,
    zoom,
    zoomIn: setZoom,
    zoomOut: () => setZoom(null),
    focusedMetric,
    focusMetric: setFocusedMetric,
    closeMetric: () => setFocusedMetric(null),
  }
}
